/**
 * Detalle de placement: datos editables, geo, estado, evidencias por etapa
 * (incluye la factura del costo en la etapa "facturación").
 */
import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Save, X, Trash2, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { AdStateBadge } from "@/components/feature/media/ad-state-badge";
import { PlacementStateTransition } from "@/components/feature/media/placement-state-transition";
import { EvidenceUploader } from "@/components/feature/media/evidence-uploader";
import { useMe } from "@/hooks/use-me";
import { media, type PlacementState, type PlacementOut, type ContentFormat } from "@/lib/api";

export const Route = createFileRoute("/_auth/media/placements_/$placementId")({
  component: PlacementDetail,
});

function PlacementDetail() {
  const { placementId } = Route.useParams();
  const id = Number(placementId);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: me } = useMe();
  const canDelete = me?.role === "owner" || me?.role === "admin" || me?.role === "superadmin";
  const isScoped = me?.role === "cliente" || me?.role === "supervisor";
  const [editing, setEditing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const placement = useQuery({
    queryKey: ["media", "placement", id],
    queryFn: () => media.getPlacement(id),
  });

  // Impacto del borrado — solo se consulta al abrir la alerta.
  const impactQ = useQuery({
    queryKey: ["media", "placement", id, "delete-impact"],
    queryFn: () => media.placementDeleteImpact(id),
    enabled: confirmOpen,
  });

  const adIdForNav = placement.data?.ad_id;
  const del = useMutation({
    mutationFn: () => media.deletePlacement(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media", "placements"] });
      if (adIdForNav) qc.invalidateQueries({ queryKey: ["media", "placements", "by_ad", adIdForNav] });
      toast.success("Placement eliminado");
      if (adIdForNav) {
        navigate({ to: "/media/ads/$adId", params: { adId: String(adIdForNav) } });
      } else {
        navigate({ to: "/media/ads" });
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const outletId = placement.data?.outlet_id;
  const outlet = useQuery({
    queryKey: ["media", "outlet", outletId],
    queryFn: () => media.listOutlets({ limit: 500 }).then(list => list.find(o => o.id === outletId)),
    enabled: !!outletId,
  });

  const adId = placement.data?.ad_id;
  const ad = useQuery({
    queryKey: ["media", "ad", adId],
    queryFn: () => media.getAd(adId!),
    enabled: !!adId,
  });

  if (placement.isLoading) return <div className="px-6 py-6 max-w-5xl mx-auto"><Skeleton className="h-64" /></div>;
  if (!placement.data) return <div className="px-6 py-6">Placement no encontrado</div>;

  const p = placement.data;
  const acceptsFormats = outlet.data?.accepts_formats ?? [];

  return (
    <div className="px-6 py-6 max-w-5xl mx-auto">
      {adId && (
        <Link to="/media/ads/$adId" params={{ adId: String(adId) }}
          className="text-xs text-ink-500 hover:text-ink-900 inline-flex items-center gap-1 mb-4">
          <ArrowLeft size={12} /> Volver al aviso
        </Link>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-3">
              <div>
                <CardTitle className="text-lg">{ad.data?.code ?? `Placement #${p.id}`}</CardTitle>
                {ad.data?.title && <p className="text-sm text-ink-500 mt-1">{ad.data.title}</p>}
              </div>
              <div className="flex items-center gap-2">
                <AdStateBadge state={p.state} />
                {!editing && (
                  <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                    <Pencil size={13} /> Editar
                  </Button>
                )}
                {canDelete && !editing && (
                  <Button size="sm" variant="destructive" title="Eliminar placement"
                    onClick={() => setConfirmOpen(true)}>
                    <Trash2 size={13} /> Eliminar
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {editing ? (
                <PlacementEditForm p={p} onDone={() => setEditing(false)} outletName={outlet.data?.name} />
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                    <Field label="Medio" value={outlet.data?.name ?? "—"} />
                    <Field label="Tipo medio" value={outlet.data?.kind ?? "—"} />
                    <Field label="Cobertura" value={p.coverage} />
                    <Field label="Fecha solicitud" value={p.requested_at ?? "—"} />
                    <Field label="Fecha programación" value={p.scheduled_at ?? "—"} />
                    <Field label="Fecha publicación" value={p.published_at ?? "—"} />
                    <Field label="Fecha pagos" value={p.payment_at ?? "—"} />
                    <Field label="Fecha finalización" value={p.end_at ?? "—"} />
                    <Field label="Duración (días)" value={p.duration_days?.toString() ?? "—"} />
                    <Field label="Repeticiones" value={String(p.repetitions)} />
                    <Field label="Duración spot (s)" value={p.spot_duration_seconds?.toString() ?? "—"} />
                    <Field label="Palabras" value={p.word_count?.toString() ?? "—"} />
                    <Field label="Formato" value={p.content_format ?? "—"} />
                    {!isScoped && <Field label="Costo (PEN)" value={p.cost_amount?.toString() ?? "—"} />}
                    <Field label="Precio (PEN)" value={p.price_amount?.toString() ?? "—"} />
                  </div>
                  {p.notes && (
                    <div className="mt-4 p-3 bg-ink-50 dark:bg-ink-800/50 rounded text-sm">
                      <div className="text-xs uppercase tracking-wide text-ink-500 mb-1">Notas</div>
                      {p.notes}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Evidencias</CardTitle>
              <p className="text-xs text-ink-500">
                La etapa <strong>Facturación</strong> es para la factura del costo en el medio.
                Las evidencias son públicas (visibles en el portal) salvo que marques “Privada”.
              </p>
            </CardHeader>
            <CardContent>
              <EvidenceUploader placementId={p.id} acceptsFormats={acceptsFormats} />
            </CardContent>
          </Card>
        </div>

        <div>
          <Card>
            <CardHeader><CardTitle className="text-base">Cambiar estado</CardTitle></CardHeader>
            <CardContent>
              <p className="text-xs text-ink-500 mb-3">
                Estado actual: <strong>{p.state}</strong>
              </p>
              <PlacementStateTransition placementId={p.id} currentState={p.state as PlacementState} />
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={confirmOpen} onOpenChange={(o) => { if (!del.isPending) setConfirmOpen(o); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-bad">
              <AlertTriangle size={18} /> Eliminar placement — acción irreversible
            </DialogTitle>
            <DialogDescription>
              Se eliminará esta inserción y <strong>sus evidencias de forma permanente</strong>.
              Esta acción no se puede deshacer. Si solo quieres dejar constancia de que
              no se realizó, usa la transición a estado <strong>cancelado</strong>.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-lg border border-bad/30 bg-bad/5 p-3 text-sm">
            {impactQ.isLoading ? (
              <Skeleton className="h-16" />
            ) : impactQ.data ? (
              <>
                <p className="font-semibold text-ink-900 dark:text-white mb-2">Se borrará:</p>
                <ul className="space-y-1 text-ink-600 dark:text-ink-300">
                  <li>· 1 placement</li>
                  <li>· {impactQ.data.evidences} evidencia(s) adjunta(s)</li>
                </ul>
                {(impactQ.data.report_assets > 0 || impactQ.data.report_indicators > 0) && (
                  <p className="text-[11px] text-ink-500 mt-2">
                    {impactQ.data.report_assets} adjunto(s) y {impactQ.data.report_indicators} indicador(es)
                    de reportes quedarán desvinculados de este placement (se conservan).
                  </p>
                )}
              </>
            ) : (
              <p className="text-ink-500">No se pudo calcular el impacto.</p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={del.isPending}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={() => del.mutate()} disabled={del.isPending || impactQ.isLoading}>
              <Trash2 size={14} /> {del.isPending ? "Eliminando…" : "Eliminar definitivamente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PlacementEditForm({
  p, onDone, outletName,
}: {
  p: PlacementOut;
  onDone: () => void;
  outletName?: string;
}) {
  const qc = useQueryClient();
  const [f, setF] = useState({
    requested_at: p.requested_at ?? "",
    scheduled_at: p.scheduled_at ?? "",
    end_at: p.end_at ?? "",
    payment_at: p.payment_at ?? "",
    duration_days: p.duration_days != null ? String(p.duration_days) : "",
    repetitions: String(p.repetitions),
    spot_duration_seconds: p.spot_duration_seconds != null ? String(p.spot_duration_seconds) : "",
    word_count: p.word_count != null ? String(p.word_count) : "",
    content_format: (p.content_format ?? "") as ContentFormat | "",
    cost_amount: p.cost_amount != null ? String(p.cost_amount) : "",
    price_amount: p.price_amount != null ? String(p.price_amount) : "",
    notes: p.notes ?? "",
  });

  const save = useMutation({
    mutationFn: () => media.patchPlacement(p.id, {
      requested_at: f.requested_at || null,
      scheduled_at: f.scheduled_at || null,
      end_at: f.end_at || null,
      payment_at: f.payment_at || null,
      duration_days: f.duration_days ? Number(f.duration_days) : null,
      repetitions: f.repetitions ? Number(f.repetitions) : 1,
      spot_duration_seconds: f.spot_duration_seconds ? Number(f.spot_duration_seconds) : null,
      word_count: f.word_count ? Number(f.word_count) : null,
      content_format: f.content_format || null,
      cost_amount: f.cost_amount ? Number(f.cost_amount) : null,
      price_amount: f.price_amount ? Number(f.price_amount) : null,
      notes: f.notes || null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media", "placement", p.id] });
      qc.invalidateQueries({ queryKey: ["media", "placements", "by_ad", p.ad_id] });
      toast.success("Placement actualizado");
      onDone();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const set = (patch: Partial<typeof f>) => setF({ ...f, ...patch });

  return (
    <div className="space-y-3">
      <p className="text-xs text-ink-500">
        Medio: <strong>{outletName ?? "—"}</strong> · Cobertura: <strong>{p.coverage}</strong> (no editables aquí).
      </p>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div><Label className="text-xs">Fecha solicitud</Label>
          <DatePicker value={f.requested_at} onChange={(e) => set({ requested_at: e.target.value })} /></div>
        <div><Label className="text-xs">Fecha programación</Label>
          <DatePicker value={f.scheduled_at} onChange={(e) => set({ scheduled_at: e.target.value })} /></div>
        <div><Label className="text-xs">Fecha finalización</Label>
          <DatePicker value={f.end_at} onChange={(e) => set({ end_at: e.target.value })} /></div>
        <div><Label className="text-xs">Fecha de pago</Label>
          <DatePicker value={f.payment_at} onChange={(e) => set({ payment_at: e.target.value })} /></div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div><Label className="text-xs">Duración (días)</Label>
          <Input type="number" min="0" value={f.duration_days} onChange={(e) => set({ duration_days: e.target.value })} /></div>
        <div><Label className="text-xs">Repeticiones</Label>
          <Input type="number" min="1" value={f.repetitions} onChange={(e) => set({ repetitions: e.target.value })} /></div>
        <div><Label className="text-xs">Spot (s)</Label>
          <Input type="number" min="0" value={f.spot_duration_seconds} onChange={(e) => set({ spot_duration_seconds: e.target.value })} /></div>
        <div><Label className="text-xs">Palabras</Label>
          <Input type="number" min="0" value={f.word_count} onChange={(e) => set({ word_count: e.target.value })} /></div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs">Formato</Label>
          <Select value={f.content_format || "none"}
            onValueChange={(v) => set({ content_format: v === "none" ? "" : v as ContentFormat })}>
            <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">—</SelectItem>
              <SelectItem value="1/8">1/8 página</SelectItem>
              <SelectItem value="1/4">1/4 página</SelectItem>
              <SelectItem value="1/2">1/2 página</SelectItem>
              <SelectItem value="full_page">Página completa</SelectItem>
              <SelectItem value="30s">Spot 30s</SelectItem>
              <SelectItem value="60s">Spot 60s</SelectItem>
              <SelectItem value="90s">Spot 90s</SelectItem>
              <SelectItem value="custom">Personalizado</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Costo (PEN)</Label>
          <Input type="number" min="0" step="0.01" value={f.cost_amount} onChange={(e) => set({ cost_amount: e.target.value })} /></div>
        <div><Label className="text-xs">Precio (PEN) — visible al cliente</Label>
          <Input type="number" min="0" step="0.01" value={f.price_amount} onChange={(e) => set({ price_amount: e.target.value })} /></div>
      </div>
      <div>
        <Label className="text-xs">Notas</Label>
        <Textarea rows={2} value={f.notes} onChange={(e) => set({ notes: e.target.value })} />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onDone}><X size={14} /> Cancelar</Button>
        <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
          <Save size={14} /> {save.isPending ? "Guardando…" : "Guardar"}
        </Button>
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-ink-500">{label}</div>
      <div className="text-sm text-ink-900 dark:text-white">{value}</div>
    </div>
  );
}
